// WALLET-P1 §6.2: Operator › Billing › Companies — one row per granted company (balance, spent
// this month, charged, cost, margin, status) from the month's margin read; per row: Open billing
// (the client page), Custom prices, Add credit / Adjust, Refund, Settings — each a modal.
import { newIdempotencyKey } from "@companyos/sdk";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  buttonVariants,
  cn,
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EmptyState,
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  InfoTip,
  Input,
  Label,
  NativeSelect,
  NativeSelectOption,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
  ArrowUpRightIcon,
  Building2Icon,
  CirclePlusIcon,
  SettingsIcon,
  TagIcon,
  Undo2Icon,
} from "lucide-react";
import { type FormEvent, useId, useState } from "react";
import type { Api } from "../../../api/client";
import {
  balanceText,
  dollars,
  maxRefundMicros,
  microsToInput,
  parseDollars,
  signedDollars,
} from "../../billing/billing-money";
import { DisabledWhy, WalletStatusChip } from "../../billing/billing-parts";
import {
  billingKeys,
  type OperatorWalletView,
  type WalletMargin,
  walletMarginQuery,
} from "../../billing/billing-queries";
import { formatDate } from "../../format";
import { problemText } from "../../ops-parts";
import { LoadError, Loading } from "../../screen-parts";
import { DENSE_TABLE, RowAction, RowActions } from "../../settings/row-actions";
import { CompanyPrices } from "./company-prices";

type Row = WalletMargin["companies"][number];
type Modal = { kind: "prices" | "adjust" | "refund" | "settings"; row: Row } | null;

export function Companies({ api, month }: { api: Api; month: string }) {
  const qc = useQueryClient();
  const margin = useQuery(walletMarginQuery(api, month));
  const [modal, setModal] = useState<Modal>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: billingKeys.operator });
  const close = () => setModal(null);
  return (
    <section className="flex min-w-0 flex-col gap-3" data-slot="billing-companies">
      <h2 className="text-sm font-semibold">Companies</h2>
      {margin.isPending ? (
        <Loading label="Loading companies" rows={3} />
      ) : margin.isError ? (
        <LoadError what="Companies" error={margin.error} />
      ) : margin.data.companies.length === 0 ? (
        <EmptyState variant="card" icon={Building2Icon} title="No companies granted" />
      ) : (
        <Table className={DENSE_TABLE} data-slot="wallet-companies">
          <TableHeader>
            <TableRow>
              <TableHead scope="col">Company</TableHead>
              <TableHead scope="col" className="text-right">
                Balance
              </TableHead>
              <TableHead scope="col" className="text-right max-md:hidden">
                Spent this month
              </TableHead>
              <TableHead scope="col" className="text-right max-lg:hidden">
                Charged
              </TableHead>
              <TableHead scope="col" className="text-right max-lg:hidden">
                Cost
              </TableHead>
              <TableHead scope="col" className="text-right max-lg:hidden">
                Margin
              </TableHead>
              <TableHead scope="col">Status</TableHead>
              <TableHead scope="col">
                <span className="sr-only">Actions</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {margin.data.companies.map((r) => (
              <TableRow key={r.company.id} data-company={r.company.slug} className="group/row">
                <TableHead scope="row" className="font-normal">
                  {r.company.name}
                </TableHead>
                <TableCell className="text-right tabular-nums">
                  {dollars(r.balance_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums max-md:hidden">
                  {dollars(r.spent_this_month_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums max-lg:hidden">
                  {dollars(r.charged_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums max-lg:hidden">
                  {dollars(r.cost_micros)}
                </TableCell>
                <TableCell className="text-right tabular-nums max-lg:hidden">
                  {dollars(r.margin_micros)}
                </TableCell>
                <TableCell>
                  <WalletStatusChip status={r.status} />
                </TableCell>
                <TableCell className="w-px text-right">
                  <RowActions>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <Link
                            to="/c/$slug/setup/billing"
                            params={{ slug: r.company.slug }}
                            aria-label={`Open ${r.company.name} billing`}
                            className={cn(
                              buttonVariants({ variant: "ghost", size: "icon-sm" }),
                              "text-muted-foreground hover:text-foreground",
                            )}
                          />
                        }
                      >
                        <ArrowUpRightIcon aria-hidden="true" />
                      </TooltipTrigger>
                      <TooltipContent>Open billing</TooltipContent>
                    </Tooltip>
                    <RowAction
                      icon={TagIcon}
                      label={`Custom prices for ${r.company.name}`}
                      tip="Custom prices"
                      onClick={() => setModal({ kind: "prices", row: r })}
                    />
                    <RowAction
                      icon={CirclePlusIcon}
                      label={`Add credit or adjust ${r.company.name}`}
                      tip="Add credit / Adjust"
                      onClick={() => setModal({ kind: "adjust", row: r })}
                    />
                    <RowAction
                      icon={Undo2Icon}
                      label={`Refund ${r.company.name}`}
                      tip="Refund"
                      onClick={() => setModal({ kind: "refund", row: r })}
                    />
                    <RowAction
                      icon={SettingsIcon}
                      label={`Wallet settings of ${r.company.name}`}
                      tip="Settings"
                      onClick={() => setModal({ kind: "settings", row: r })}
                    />
                  </RowActions>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <CompanyPrices
        api={api}
        open={modal?.kind === "prices"}
        company={modal ? { id: modal.row.company.id, name: modal.row.company.name } : null}
        onClose={close}
      />
      <Dialog
        open={modal?.kind === "adjust" || modal?.kind === "refund" || modal?.kind === "settings"}
        onOpenChange={(o) => (o ? null : close())}
      >
        <DialogContent data-slot={`wallet-${modal?.kind ?? "modal"}`}>
          {modal?.kind === "adjust" ? (
            <AdjustForm
              api={api}
              row={modal.row}
              onDone={() => {
                close();
                refresh();
              }}
            />
          ) : modal?.kind === "refund" ? (
            <RefundForm
              api={api}
              row={modal.row}
              onDone={() => {
                close();
                refresh();
              }}
            />
          ) : modal?.kind === "settings" ? (
            <SettingsForm
              api={api}
              row={modal.row}
              onDone={() => {
                close();
                refresh();
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </section>
  );
}

const operatorWalletQuery = (api: Api, companyId: string) => ({
  queryKey: billingKeys.operatorWallet(companyId),
  queryFn: () => api.client.operator.wallet.get({ params: { company_id: companyId } }),
});

function AdjustForm({ api, row, onDone }: { api: Api; row: Row; onDone: () => void }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [key] = useState(() => newIdempotencyKey());
  const ids = { amount: useId(), reason: useId() };
  const micros = parseDollars(amount);
  const valid =
    micros !== null && micros !== 0 && Math.abs(micros) <= 10_000_000_000 && reason.trim() !== "";
  const adjust = useMutation({
    mutationFn: () =>
      api.client.operator.wallet.adjust({
        params: { company_id: row.company.id },
        idempotencyKey: key,
        body: { amount_micros: micros ?? 0, reason: reason.trim() },
      }),
    onSuccess: onDone,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) adjust.mutate();
  };
  return (
    <form onSubmit={submit} className="grid gap-4">
      <DialogHeader>
        <DialogTitle>Add credit / Adjust · {row.company.name}</DialogTitle>
      </DialogHeader>
      <FieldGroup>
        <Field data-invalid={amount !== "" && (micros === null || micros === 0)}>
          <FieldLabel htmlFor={ids.amount}>Amount ($)</FieldLabel>
          <Input
            id={ids.amount}
            inputMode="decimal"
            required
            value={amount}
            placeholder="25.00 or -10.00"
            onChange={(e) => setAmount(e.target.value)}
          />
          <FieldDescription>
            Balance {balanceText(row.balance_micros)}. Negative to take credit back.
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.reason}>Reason</FieldLabel>
          <Textarea
            id={ids.reason}
            rows={2}
            required
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        {adjust.isError ? (
          <FieldError>{problemText(adjust.error, "The adjustment was not posted.")}</FieldError>
        ) : null}
      </FieldGroup>
      <DialogFooter>
        <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
        <Button type="submit" disabled={!valid || adjust.isPending}>
          {adjust.isPending
            ? "Posting…"
            : micros !== null && micros < 0
              ? `Debit ${signedDollars(micros)}`
              : "Add credit"}
        </Button>
      </DialogFooter>
    </form>
  );
}

function RefundForm({ api, row, onDone }: { api: Api; row: Row; onDone: () => void }) {
  const ov = useQuery(operatorWalletQuery(api, row.company.id));
  if (ov.isPending) return <Loading label="Loading payments" rows={2} />;
  if (ov.isError) return <LoadError what="Payments" error={ov.error} />;
  return <RefundFields api={api} row={row} view={ov.data} onDone={onDone} />;
}

function RefundFields({
  api,
  row,
  view,
  onDone,
}: {
  api: Api;
  row: Row;
  view: OperatorWalletView;
  onDone: () => void;
}) {
  const paid = view.checkouts.filter((c) => c.amount_micros > c.refunded_micros);
  const [checkout, setCheckout] = useState(paid[0]?.id ?? "");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [confirm, setConfirm] = useState(false);
  const qc = useQueryClient();
  // A fresh request per attempt that ended in an error: one still unsettled is listed under
  // "In progress" (its key kept on the server) and resumed there; one that failed for good
  // leaves the form free for a new request (Codex r20 #2).
  const [key, setKey] = useState(() => newIdempotencyKey());
  const ids = { checkout: useId(), amount: useId(), reason: useId() };
  const picked = paid.find((c) => c.id === checkout);
  // The refund plus the bonus it claws back must fit in the credit that is free to take back.
  const cap = picked
    ? maxRefundMicros({ ...picked, available_micros: view.wallet.available_micros })
    : 0;
  const micros = parseDollars(amount);
  const amountOk = micros !== null && micros > 0 && micros <= cap && micros % 10_000 === 0;
  const valid = picked !== undefined && amountOk && reason.trim() !== "";
  const refund = useMutation({
    mutationFn: () =>
      api.client.operator.wallet.refund({
        params: { company_id: row.company.id },
        idempotencyKey: key,
        body: { checkout_id: checkout, amount_micros: micros ?? 0, reason: reason.trim() },
      }),
    onSuccess: () => {
      setConfirm(false);
      onDone();
    },
    onError: () => {
      setConfirm(false);
      setKey(newIdempotencyKey());
      void qc.invalidateQueries({ queryKey: billingKeys.operatorWallet(row.company.id) });
    },
  });
  // A refund whose answer from Stripe was lost stays held under its own key: Resume sends the
  // same request again, from any session (the key lives on the server, not in this dialog).
  const resume = useMutation({
    mutationFn: (p: OperatorWalletView["pending_refunds"][number]) =>
      api.client.operator.wallet.refund({
        params: { company_id: row.company.id },
        idempotencyKey: p.request_key,
        body: { checkout_id: p.checkout_id, amount_micros: p.amount_micros, reason: p.reason },
      }),
    onSuccess: () => onDone(),
  });
  const pending = view.pending_refunds.length ? (
    <Field>
      <FieldLabel>
        In progress
        <InfoTip label="About refunds in progress">
          Stripe's answer was not received. The amount stays held until the refund is resumed or
          Stripe confirms it.
        </InfoTip>
      </FieldLabel>
      <ul className="grid gap-2">
        {view.pending_refunds.map((p) => (
          <li key={p.request_key} className="flex items-center justify-between gap-3 text-sm">
            <span>
              {formatDate(p.created_at)} · {dollars(p.amount_micros)} · {p.reason}
            </span>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={resume.isPending}
              onClick={() => resume.mutate(p)}
            >
              Resume
            </Button>
          </li>
        ))}
      </ul>
      {resume.isError ? (
        <FieldError>{problemText(resume.error, "The refund is still in progress.")}</FieldError>
      ) : null}
    </Field>
  ) : null;
  if (paid.length === 0)
    return (
      <>
        <DialogHeader>
          <DialogTitle>Refund · {row.company.name}</DialogTitle>
        </DialogHeader>
        {pending ? <FieldGroup>{pending}</FieldGroup> : null}
        <EmptyState icon={Undo2Icon} title="No paid top-ups to refund" />
        <DialogFooter>
          <DialogClose render={<Button variant="secondary" type="button" />}>Close</DialogClose>
        </DialogFooter>
      </>
    );
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) setConfirm(true);
  };
  return (
    <form onSubmit={submit} className="grid gap-4">
      <DialogHeader>
        <DialogTitle>Refund · {row.company.name}</DialogTitle>
      </DialogHeader>
      <FieldGroup>
        {pending}
        <Field>
          <FieldLabel htmlFor={ids.checkout}>Payment</FieldLabel>
          <NativeSelect
            id={ids.checkout}
            value={checkout}
            onChange={(e) => setCheckout(e.target.value)}
          >
            {paid.map((c) => (
              <NativeSelectOption key={c.id} value={c.id}>
                {formatDate(c.completed_at)} · {dollars(c.amount_micros)}
                {c.refunded_micros > 0 ? ` (${dollars(c.refunded_micros)} refunded)` : ""}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </Field>
        <Field data-invalid={amount !== "" && !amountOk}>
          <FieldLabel htmlFor={ids.amount}>Amount ($)</FieldLabel>
          <Input
            id={ids.amount}
            inputMode="decimal"
            required
            value={amount}
            placeholder={microsToInput(cap)}
            onChange={(e) => setAmount(e.target.value)}
          />
          <FieldDescription>
            At most {dollars(cap)}
            <InfoTip label="About the refund limit">
              Limited by the unspent credit; refunding also takes back its share of the bonus.
            </InfoTip>
          </FieldDescription>
        </Field>
        <Field>
          <FieldLabel htmlFor={ids.reason}>Reason</FieldLabel>
          <Textarea
            id={ids.reason}
            rows={2}
            required
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </Field>
        {refund.isError ? (
          <FieldError>{problemText(refund.error, "The refund was not made.")}</FieldError>
        ) : null}
      </FieldGroup>
      <DialogFooter>
        <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
        <Button type="submit" variant="destructive" disabled={!valid}>
          Refund
        </Button>
      </DialogFooter>
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Refund {micros !== null ? dollars(micros) : ""} to the card?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The credit leaves {row.company.name}'s balance now, with its bonus share.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={refund.isPending}
              onClick={() => refund.mutate()}
            >
              {refund.isPending ? "Refunding…" : "Refund"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </form>
  );
}

function SettingsForm({ api, row, onDone }: { api: Api; row: Row; onDone: () => void }) {
  const ov = useQuery(operatorWalletQuery(api, row.company.id));
  if (ov.isPending) return <Loading label="Loading settings" rows={2} />;
  if (ov.isError) return <LoadError what="Settings" error={ov.error} />;
  return <SettingsFields api={api} row={row} view={ov.data} onDone={onDone} />;
}

function SettingsFields({
  api,
  row,
  view,
  onDone,
}: {
  api: Api;
  row: Row;
  view: OperatorWalletView;
  onDone: () => void;
}) {
  const [enabled, setEnabled] = useState(view.wallet.enabled);
  const [min, setMin] = useState(microsToInput(view.wallet.min_topup_micros));
  const [max, setMax] = useState(microsToInput(view.wallet.max_topup_micros));
  const ids = { enabled: useId(), min: useId(), max: useId() };
  const minM = parseDollars(min);
  const maxM = parseDollars(max);
  const valid = minM !== null && maxM !== null && minM > 0 && maxM >= minM;
  const save = useMutation({
    mutationFn: () =>
      api.client.operator.wallet.putSettings({
        params: { company_id: row.company.id },
        body: { enabled, min_topup_micros: minM ?? 0, max_topup_micros: maxM ?? 0 },
      }),
    onSuccess: onDone,
  });
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (valid) save.mutate();
  };
  const toggle = (
    <Switch
      id={ids.enabled}
      checked={enabled}
      disabled={view.home_company}
      onCheckedChange={(v) => setEnabled(Boolean(v))}
    />
  );
  return (
    <form onSubmit={submit} className="grid gap-4">
      <DialogHeader>
        <DialogTitle>Wallet settings · {row.company.name}</DialogTitle>
      </DialogHeader>
      <FieldGroup>
        <div className="flex items-center justify-between gap-4">
          <Label htmlFor={ids.enabled}>Credit on</Label>
          {view.home_company ? (
            <DisabledWhy why="The operator's own company keeps the wallet off">
              {toggle}
            </DisabledWhy>
          ) : (
            toggle
          )}
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field data-invalid={minM === null || minM <= 0}>
            <FieldLabel htmlFor={ids.min}>Minimum top-up ($)</FieldLabel>
            <Input
              id={ids.min}
              inputMode="decimal"
              value={min}
              onChange={(e) => setMin(e.target.value)}
            />
          </Field>
          <Field data-invalid={maxM === null || (minM !== null && maxM < minM)}>
            <FieldLabel htmlFor={ids.max}>Maximum top-up ($)</FieldLabel>
            <Input
              id={ids.max}
              inputMode="decimal"
              value={max}
              onChange={(e) => setMax(e.target.value)}
            />
          </Field>
        </div>
        {save.isError ? (
          <FieldError>{problemText(save.error, "The settings were not saved.")}</FieldError>
        ) : null}
      </FieldGroup>
      <DialogFooter>
        <DialogClose render={<Button variant="secondary" type="button" />}>Cancel</DialogClose>
        <Button type="submit" disabled={!valid || save.isPending}>
          {save.isPending ? "Saving…" : "Save"}
        </Button>
      </DialogFooter>
    </form>
  );
}
